import { inject, Injectable } from '@angular/core';
import { ForbiddenEventModalComponent } from '@core/modals/forbidden-event-modal/forbidden-event-modal.component';
import { AddDigitalWalletModalComponent } from '@features/pages/payment/components/add-digital-wallet-modal/add-digital-wallet-modal.component';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import {
  ModalData,
  PatronModel,
  SeatAvailability,
  SeatWithDefinitions,
} from '../models';
import { GaModalComponent } from './ga-modal/ga-modal.component';
import { LegalTextModalComponent } from './legal-text/legal-text.component';
import { ModalsComponent } from './modals.component';
import { RestrictedSeatsModalComponent } from './restricted-seats-modal/restricted-seats-modal.component';
import { SeatDefinitionsModalComponent } from './seat-definitions-modal/seat-definitions-modal.component';
import { TutorialWelcomeModalComponent } from './tutorial-welcome-modal/tutorial-welcome-modal.component';

export interface RestrictedSeatsModalPayload {
  func: () => void;
  seats: SeatAvailability[] | SeatWithDefinitions[];
}

@Injectable({
  providedIn: 'root',
})
export class ModalsService {
  bsModalRef?: BsModalRef;
  private modalService = inject(BsModalService);

  constructor() {}

  openModal({ ...modalData }: ModalData) {
    const initialState: ModalOptions = {
      initialState: modalData,
      backdrop: true,
      ignoreBackdropClick: true,
      class: 'modal-dialog-centered modal-lg',
    };
    this.bsModalRef = this.modalService.show(ModalsComponent, initialState);
  }

  openAddDigitalWalletModal(
    patron: PatronModel,
    acceptFunc: (digitalWalletList: PatronModel['digitalWalletList']) => void
  ) {
    const initialState: ModalOptions = {
      initialState: { patron, acceptFunc },
      backdrop: true,
      ignoreBackdropClick: true,
      class: 'modal-dialog-centered modal-lg',
    };
    this.bsModalRef = this.modalService.show(
      AddDigitalWalletModalComponent,
      initialState
    );
  }

  openGaModal({ ...modalData }: ModalData) {
    const initialState: ModalOptions = {
      initialState: modalData,
      backdrop: true,
      ignoreBackdropClick: true,
      class: 'modal-dialog-centered modal-lg',
    };
    this.bsModalRef = this.modalService.show(GaModalComponent, initialState);
  }

  openSeatDefinitionModal(
    { seatDefinition, isAda }: SeatAvailability,
    seatId: string
  ) {
    const modalData = {
      definitions: seatDefinition,
      isAda,
      seatId,
    };
    const initialState: ModalOptions = {
      initialState: modalData,
      backdrop: true,
      ignoreBackdropClick: true,
      class: 'modal-dialog-centered modal-sm',
    };
    this.bsModalRef = this.modalService.show(
      SeatDefinitionsModalComponent,
      initialState
    );
  }

  openTutorialWelcomeModal({ ...modalData }) {
    const initialState: ModalOptions = {
      initialState: modalData,
      backdrop: true,
      ignoreBackdropClick: true,
      class: 'modal-dialog-centered modal-lg',
    };
    this.bsModalRef = this.modalService.show(
      TutorialWelcomeModalComponent,
      initialState
    );
  }

  openRestrictedSeatsModal({ func, seats }: RestrictedSeatsModalPayload) {
    const modalData = {
      acceptFunc: func,
      seats,
    };
    const initialState: ModalOptions = {
      initialState: modalData,
      backdrop: true,
      ignoreBackdropClick: true,
      class: 'modal-dialog-centered modal-lg',
    };
    this.bsModalRef = this.modalService.show(
      RestrictedSeatsModalComponent,
      initialState
    );
  }

  openForbiddenEventModal() {
    const initialState: ModalOptions = {
      backdrop: true,
      ignoreBackdropClick: true,
      class: 'modal-dialog-centered modal-lg',
    };
    this.bsModalRef = this.modalService.show(
      ForbiddenEventModalComponent,
      initialState
    );
  }

  openLegalTextModal() {
    const initialState = {
      backdrop: true,
      ignoreBackdropClick: true,
      class: 'modal-dialog-centered modal-xl',
    };
    this.bsModalRef = this.modalService.show(
      LegalTextModalComponent,
      initialState
    );
  }

  closeModal() {
    if (!this.bsModalRef) {
      return;
    }
    this.modalService.hide(this.bsModalRef.id);
  }
}
